import { useRouter } from 'next/router'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import FAQIcon from 'assets/svg/app/dashboard/faq.svg'
import HistoryIcon from 'assets/svg/app/dashboard/history.svg'
import LeaderboardIcon from 'assets/svg/app/dashboard/leaderboard.svg'
import MarketsIcon from 'assets/svg/app/dashboard/markets.svg'
import OrdersIcon from 'assets/svg/app/dashboard/orders.svg'
import OverviewIcon from 'assets/svg/app/dashboard/overview.svg'
import PoolsIcon from 'assets/svg/app/dashboard/pools.svg'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import MenuButton from 'components/Nav/MenuButton'
import { TabList, TabPanel } from 'components/Tab'
import { MENU_BUTTON_FADE_TIME } from 'constants/menu'
import ROUTES from 'constants/routes'
import { HistoryTabs } from 'sections/dashboard/History/HistoryTabs'
import AppLayout from 'sections/shared/Layout/AppLayout'
import { selectConditionalOrderTableItems } from 'state/futures/selectors'
import { useAppSelector } from 'state/hooks'
import { LeftSideContent, PageContent } from 'styles/common'

import { MarketTabs } from './Markets/MarketsTab'

enum Tab {
	Overview = 'overview',
	Markets = 'markets',
	Orders = 'orders',
	Leaderboard = 'leaderboard',
	Pools = 'pools',
	History = 'history',
	FAQ = 'faq',
}

const Tabs = Object.values(Tab)

const DashboardLayout: FC<{ children?: ReactNode }> = ({ children }) => {
	const { t } = useTranslation()
	const router = useRouter()
	const [fade, setFade] = useState(true)
	const [activeUrl, setActiveUrl] = useState<string>()

	const orders = useAppSelector(selectConditionalOrderTableItems)

	const tabQuery = useMemo(() => {
		if (router.pathname) {
			const tab = router.pathname.split('/')[2] as Tab
			if (Tabs.includes(tab)) return tab
		}
		return null
	}, [router.pathname])

	const handleOnMenuClick = useCallback(
		(url: string) => {
			if (url === activeUrl) {
				return
			}

			setFade(true)
			setActiveUrl(url)
			setTimeout(() => {
				router.push(url)
			}, MENU_BUTTON_FADE_TIME)
		},
		[router, activeUrl]
	)

	useEffect(() => {
		setTimeout(() => setFade(false), MENU_BUTTON_FADE_TIME)
		setActiveUrl(tabQuery ?? Tab.Overview)
	}, [tabQuery])

	const activeTab = useMemo(() => {
		if (activeUrl) {
			const tab = activeUrl.split('/')[2] as Tab
			if (Tabs.includes(tab)) return tab
		}

		return tabQuery ?? Tab.Overview
	}, [tabQuery, activeUrl])

	const TABS = useMemo(
		() => [
			{
				name: Tab.Overview,
				label: t('dashboard.tabs.overview'),
				active: activeTab === Tab.Overview,
				href: ROUTES.Dashboard.Home,
				icon: <OverviewIcon />,
			},
			{
				name: Tab.Markets,
				label: t('dashboard.tabs.markets'),
				active: activeTab === Tab.Markets,
				disabled: false,
				href: ROUTES.Dashboard.Markets(MarketTabs.ALL_MARKETS),
				icon: <MarketsIcon />,
			},
			{
				name: Tab.Orders,
				label: t('dashboard.tabs.orders'),
				active: activeTab === Tab.Orders,
				disabled: false,
				href: ROUTES.Dashboard.Orders,
				icon: <OrdersIcon />,
				count: orders.length,
			},
			{
				name: Tab.Pools,
				label: t('dashboard.tabs.pools'),
				active: activeTab === Tab.Pools,
				disabled: true,
				href: ROUTES.Dashboard.Pools,
				icon: <PoolsIcon />,
			},
			{
				name: Tab.History,
				label: t('dashboard.tabs.history'),
				active: activeTab === Tab.History,
				href: ROUTES.Dashboard.History(HistoryTabs.POSITIONS),
				icon: <HistoryIcon />,
			},
			{
				name: Tab.Leaderboard,
				label: t('dashboard.tabs.leaderboard'),
				active: activeTab === Tab.Leaderboard,
				disabled: false,
				href: ROUTES.Dashboard.Leaderboard,
				icon: <LeaderboardIcon />,
			},
			{
				name: Tab.FAQ,
				label: t('dashboard.tabs.faq'),
				active: activeTab === Tab.FAQ,
				disabled: true,
				href: ROUTES.Dashboard.FAQ,
				icon: <FAQIcon />,
			},
		],
		[t, activeTab, orders.length]
	)

	return (
		<AppLayout>
			<DesktopOnlyView>
				<PageContent>
					<StyledFullHeightContainer>
						<StyledLeftSideContent>
							<StyledTabList>
								{TABS.filter((t) => !t.disabled).map(({ name, label, active, ...rest }) => (
									<MenuButton
										key={name}
										title={label}
										isActive={active}
										{...rest}
										onClick={handleOnMenuClick}
									/>
								))}
							</StyledTabList>
						</StyledLeftSideContent>
						<MainContent fade={fade}>
							<TabPanel name={activeTab} activeTab={activeTab}>
								{children}
							</TabPanel>
						</MainContent>
					</StyledFullHeightContainer>
				</PageContent>
			</DesktopOnlyView>
			<MobileOrTabletView>{children}</MobileOrTabletView>
		</AppLayout>
	)
}

const StyledTabList = styled(TabList)`
	display: flex;
	flex-direction: column;
	margin-bottom: 12px;
`

const MainContent = styled.div<{ fade: boolean }>`
	overflow-y: scroll;
	scrollbar-width: none;
	margin: 0 auto;
	width: 100%;
	padding: 0 80px;
	opacity: ${(props) => (props.fade ? 0 : 1)};
	transition: opacity ${MENU_BUTTON_FADE_TIME}ms ease-in-out;
`

const StyledLeftSideContent = styled(LeftSideContent)`
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
	& > div > div {
		border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	}
`

const StyledFullHeightContainer = styled.div`
	display: grid;
	grid-template-columns: 150px 1fr;
	height: 100%;
`

export default DashboardLayout
